import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { clientsAPI, packagesAPI, uploadAPI } from '../../services/api';
import { Card, Button } from '../ui';
import { handleApiError, showToast } from '../../utils/helpers';

const CreateClientPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isRTL } = useLanguage();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [packages, setPackages] = useState([]);
  const [loadingPackages, setLoadingPackages] = useState(true);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  const [formData, setFormData] = useState({
    company_name: '',
    description: '',
    logo_url: '',
    // Contact user info
    contact_name: '',
    contact_email: '',
    contact_password: '',
    confirm_password: '',
    phone: '',
    whatsapp_number: '',
    // Website and social media
    website: '',
    instagram: '',
    twitter: '',
    facebook: '',
    snapchat: '',
    tiktok: '',
    linkedin: '',
    youtube: '',
    store_platform: '', // 'zid' | 'salla' | 'other'
    // Package
    package_id: '',
  });

  useEffect(() => {
    fetchPackages();
  }, []);

  const fetchPackages = async () => {
    try {
      setLoadingPackages(true);
      const response = await packagesAPI.getAll();
      setPackages(response.data.packages || []);
    } catch (err) {
      console.error('Error fetching packages:', err);
    } finally {
      setLoadingPackages(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setError(null);
  };

  const handleLogoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      showToast.error(isRTL ? 'يرجى اختيار ملف صورة' : 'Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      showToast.error(isRTL ? 'حجم الملف يجب أن يكون أقل من 5MB' : 'File size must be less than 5MB');
      return;
    }

    setLogoFile(file);
    
    // Create preview URL
    const reader = new FileReader();
    reader.onloadend = () => {
      setLogoPreview(reader.result);
    };
    reader.readAsDataURL(file);
    
    setError(null);
  };

  const handleRemoveLogo = () => {
    setLogoFile(null);
    setLogoPreview(null);
    setFormData({ ...formData, logo_url: '' });
  };

  const validateForm = () => {
    if (!formData.company_name.trim()) {
      setError(isRTL ? 'اسم الشركة مطلوب' : 'Company name is required');
      return false;
    }

    // User account is required
    if (!formData.contact_name.trim()) {
      setError(isRTL ? 'اسم جهة الاتصال مطلوب' : 'Contact name is required');
      return false;
    }

    if (!formData.contact_email.trim()) {
      setError(isRTL ? 'البريد الإلكتروني لجهة الاتصال مطلوب' : 'Contact email is required');
      return false;
    }

    if (!formData.contact_password) {
      setError(isRTL ? 'كلمة المرور مطلوبة' : 'Password is required');
      return false;
    }

    if (!formData.confirm_password) {
      setError(isRTL ? 'تأكيد كلمة المرور مطلوب' : 'Password confirmation is required');
      return false;
    }

    if (formData.contact_password.length < 6) {
      setError(isRTL ? 'كلمة المرور يجب أن تكون 6 أحرف على الأقل' : 'Password must be at least 6 characters');
      return false;
    }

    if (formData.contact_password !== formData.confirm_password) {
      setError(isRTL ? 'كلمات المرور غير متطابقة' : 'Passwords do not match');
      return false;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contact_email)) {
      setError(isRTL ? 'البريد الإلكتروني غير صحيح' : 'Invalid email format');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!validateForm()) {
      return;
    }

    setSubmitting(true);

    try {
      // Upload logo if file is selected
      let logoUrl = formData.logo_url.trim() || null;
      if (logoFile) {
        setUploadingLogo(true);
        try {
          const uploadResponse = await uploadAPI.uploadImage(logoFile);
          logoUrl = uploadResponse.data.url;
        } catch (uploadError) {
          setError(handleApiError(uploadError));
          setSubmitting(false);
          setUploadingLogo(false);
          return;
        } finally {
          setUploadingLogo(false);
        }
      }

      // Build social media accounts object (including store platform)
      const socialMediaAccounts = {};
      if (formData.instagram.trim()) socialMediaAccounts.instagram = formData.instagram.trim();
      if (formData.twitter.trim()) socialMediaAccounts.twitter = formData.twitter.trim();
      if (formData.facebook.trim()) socialMediaAccounts.facebook = formData.facebook.trim();
      if (formData.snapchat.trim()) socialMediaAccounts.snapchat = formData.snapchat.trim();
      if (formData.tiktok.trim()) socialMediaAccounts.tiktok = formData.tiktok.trim();
      if (formData.linkedin.trim()) socialMediaAccounts.linkedin = formData.linkedin.trim();
      if (formData.youtube.trim()) socialMediaAccounts.youtube = formData.youtube.trim();
      if (formData.store_platform) socialMediaAccounts.store_platform = formData.store_platform;

      const submitData = {
        company_name: formData.company_name.trim(),
        description: formData.description.trim() || null,
        logo_url: logoUrl,
        phone: formData.phone.trim() || null,
        whatsapp_number: formData.whatsapp_number.trim() || null,
        website: formData.website.trim() || null,
        social_media_accounts: Object.keys(socialMediaAccounts).length > 0 ? socialMediaAccounts : null,
        contact_name: formData.contact_name.trim() || null,
        contact_email: formData.contact_email.trim() || null,
        contact_password: formData.contact_password || null,
        package_id: formData.package_id || null,
      };

      const response = await clientsAPI.create(submitData);
      
      // Navigate to the new client
      navigate(`/clients/${response.data.client.id}`);
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <Card className="p-5 bg-gradient-to-r from-sky-50 via-white to-emerald-50 border-slate-200">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div className={isRTL ? 'text-right' : 'text-left'}>
            <h1 className="text-2xl md:text-3xl font-extrabold text-slate-900">
              {isRTL ? 'إضافة عميل جديد' : 'Add New Client'}
            </h1>
            <p className="text-slate-600 mt-1 text-sm">
              {isRTL
                ? 'إنشاء حساب عميل جديد مع تفاصيل التواصل والحسابات'
                : 'Create a new client with contact details and accounts'}
            </p>
          </div>
        </div>
      </Card>

      <form onSubmit={handleSubmit}>
        <Card className="p-6 space-y-6">
          {error && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {/* Company Information Section */}
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              {isRTL ? 'معلومات الشركة' : 'Company Information'}
            </h2>
            <div className="space-y-4">
              {/* Company Name */}
              <div>
                <label htmlFor="company_name" className="block text-sm font-medium text-gray-700 mb-2">
                  {isRTL ? 'اسم الشركة' : 'Company Name'} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="company_name"
                  name="company_name"
                  required
                  value={formData.company_name}
                  onChange={handleChange}
                  placeholder={isRTL ? 'مثال: شركة أكمي' : 'e.g., Acme Corporation'}
                  className="input"
                />
              </div>

              {/* Description */}
              <div>
                <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
                  {isRTL ? 'الوصف' : 'Description'}
                </label>
                <textarea
                  id="description"
                  name="description"
                  rows="3"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder={isRTL ? 'وصف مختصر عن العميل...' : 'Brief description of the client...'}
                  className="textarea"
                />
              </div>

              {/* Phone */}
              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                  {isRTL ? 'رقم الجوال' : 'Phone Number'}
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder={isRTL ? 'مثال: 0501234567' : 'e.g., 0501234567'}
                  className="input"
                />
              </div>

              {/* WhatsApp Number */}
              <div>
                <label htmlFor="whatsapp_number" className="block text-sm font-medium text-gray-700 mb-2">
                  {isRTL ? 'رقم واتساب' : 'WhatsApp Number'}
                </label>
                <input
                  type="tel"
                  id="whatsapp_number"
                  name="whatsapp_number"
                  value={formData.whatsapp_number}
                  onChange={handleChange}
                  placeholder={isRTL ? 'مثال: 966501234567 (مع كود الدولة)' : 'e.g., 966501234567 (with country code)'}
                  className="input"
                />
                <p className="text-xs text-gray-500 mt-1">
                  {isRTL ? 'رقم واتساب العميل مع كود الدولة (للمشاركة المباشرة)' : "Client's WhatsApp number with country code (for direct sharing)"}
                </p>
              </div>

              {/* Website + Store Platform */}
              <div>
                <label htmlFor="website" className="block text-sm font-medium text-gray-700 mb-2">
                  {isRTL ? 'الموقع الإلكتروني' : 'Website'}
                </label>
                <input
                  type="url"
                  id="website"
                  name="website"
                  value={formData.website}
                  onChange={handleChange}
                  placeholder={isRTL ? 'https://example.com' : 'https://example.com'}
                  className="input"
                />

                {/* Store Platform */}
                <div className="mt-3">
                  <p className="block text-sm font-medium text-gray-700 mb-2">
                    {isRTL ? 'منصة المتجر' : 'Store Platform'}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {[
                      { id: 'zid', ar: 'زد', en: 'Zid' },
                      { id: 'salla', ar: 'سلة', en: 'Salla' },
                      { id: '3tech', ar: '3تك', en: '3Tech' },
                      { id: 'other', ar: 'منصة أخرى', en: 'Other' },
                    ].map((p) => {
                      const isSelected = formData.store_platform === p.id;
                      return (
                        <button
                          key={p.id}
                          type="button"
                          onClick={() => setFormData({ ...formData, store_platform: p.id })}
                          className={`
                            inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium border transition-all
                            ${
                              isSelected
                                ? 'border-sky-500 bg-sky-50 text-sky-700 shadow-sm'
                                : 'border-gray-200 bg-white text-gray-700 hover:border-sky-200 hover:bg-sky-50'
                            }
                          `}
                        >
                          {isRTL ? p.ar : p.en}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Logo Upload */}
              <div>
                <label htmlFor="logo_file" className="block text-sm font-medium text-gray-700 mb-2">
                  {isRTL ? 'شعار الشركة (اختياري)' : 'Company Logo (Optional)'}
                </label>
                {logoPreview ? (
                  <div className="space-y-2">
                    <div className="relative inline-block">
                      <img
                        src={logoPreview}
                        alt="Logo preview"
                        className="w-32 h-32 object-contain border-2 border-gray-200 rounded-lg"
                      />
                    </div>
                    <div>
                      <button
                        type="button"
                        onClick={handleRemoveLogo}
                        className="text-sm text-red-600 hover:text-red-700"
                      >
                        {isRTL ? 'إزالة الشعار' : 'Remove Logo'}
                      </button>
                    </div>
                  </div>
                ) : (
                  <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100">
                    <div className="flex flex-col items-center justify-center pt-5 pb-6">
                      <svg
                        className="w-10 h-10 mb-3 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                        />
                      </svg>
                      <p className="mb-2 text-sm text-gray-500">
                        <span className="font-semibold">{isRTL ? 'انقر لرفع الشعار' : 'Click to upload logo'}</span>
                      </p>
                      <p className="text-xs text-gray-500">{isRTL ? 'PNG, JPG, GIF (حد أقصى 5MB)' : 'PNG, JPG, GIF (MAX. 5MB)'}</p>
                    </div>
                    <input
                      type="file"
                      id="logo_file"
                      name="logo_file"
                      accept="image/*"
                      onChange={handleLogoChange}
                      className="hidden"
                      disabled={uploadingLogo || submitting}
                    />
                  </label>
                )}
                {uploadingLogo && (
                  <p className="text-xs text-gray-500 mt-1">{isRTL ? 'جاري رفع الصورة...' : 'Uploading image...'}</p>
                )}
              </div>
            </div>
          </div>

          {/* Contact User Account Section */}
          <div className="pt-6 border-t border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900 mb-1">
              {isRTL ? 'حساب المستخدم' : 'User Account'}
            </h2>
            <p className="text-sm text-gray-600 mb-4">
              {isRTL 
                ? 'سيتم إنشاء حساب دخول للعميل باستخدام هذه البيانات.'
                : 'A login account will be created for the client using these details.'}
            </p>
            <div className="space-y-4">
              {/* Contact Name */}
              <div>
                <label htmlFor="contact_name" className="block text-sm font-medium text-gray-700 mb-2">
                  {isRTL ? 'اسم جهة الاتصال' : 'Contact Name'} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="contact_name"
                  name="contact_name"
                  value={formData.contact_name}
                  onChange={handleChange}
                  placeholder={isRTL ? 'مثال: أحمد محمد' : 'e.g., Ahmed Mohammed'}
                  className="input"
                />
              </div>

              {/* Email */}
              <div>
                <label htmlFor="contact_email" className="block text-sm font-medium text-gray-700 mb-2">
                  {isRTL ? 'البريد الإلكتروني' : 'Email'} <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  id="contact_email"
                  name="contact_email"
                  value={formData.contact_email}
                  onChange={handleChange}
                  placeholder="client@example.com"
                  className="input"
                />
              </div>

              {/* Password */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="contact_password" className="block text-sm font-medium text-gray-700 mb-2">
                    {isRTL ? 'كلمة المرور' : 'Password'} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="password"
                    id="contact_password"
                    name="contact_password"
                    value={formData.contact_password}
                    onChange={handleChange}
                    placeholder={isRTL ? '6 أحرف على الأقل' : 'At least 6 characters'}
                    className="input"
                  />
                </div>
                <div>
                  <label htmlFor="confirm_password" className="block text-sm font-medium text-gray-700 mb-2">
                    {isRTL ? 'تأكيد كلمة المرور' : 'Confirm Password'} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="password"
                    id="confirm_password"
                    name="confirm_password"
                    value={formData.confirm_password}
                    onChange={handleChange}
                    placeholder={isRTL ? 'أعد إدخال كلمة المرور' : 'Re-enter password'}
                    className="input"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Social Media Accounts Section */}
          <div className="pt-6 border-t border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              {isRTL ? 'حسابات التواصل الاجتماعي (اختياري)' : 'Social Media Accounts (Optional)'}
            </h2>
            <p className="text-sm text-gray-600 mb-4">
              {isRTL 
                ? 'أضف معرفات حسابات التواصل الاجتماعي للعميل. سيتم استخدام هذه المعلومات في معاينة التصاميم.'
                : 'Add social media account handles for the client. This information will be used in design previews.'}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Instagram */}
              <div>
                <label htmlFor="instagram" className="block text-sm font-medium text-gray-700 mb-2">
                  <span className="inline-block w-5">📷</span> Instagram
                </label>
                <input
                  type="text"
                  id="instagram"
                  name="instagram"
                  value={formData.instagram}
                  onChange={handleChange}
                  placeholder="@username"
                  className="input"
                />
              </div>

              {/* Twitter/X */}
              <div>
                <label htmlFor="twitter" className="block text-sm font-medium text-gray-700 mb-2">
                  <span className="inline-block w-5">🐦</span> Twitter/X
                </label>
                <input
                  type="text"
                  id="twitter"
                  name="twitter"
                  value={formData.twitter}
                  onChange={handleChange}
                  placeholder="@handle"
                  className="input"
                />
              </div>

              {/* Facebook */}
              <div>
                <label htmlFor="facebook" className="block text-sm font-medium text-gray-700 mb-2">
                  <span className="inline-block w-5">👥</span> Facebook
                </label>
                <input
                  type="text"
                  id="facebook"
                  name="facebook"
                  value={formData.facebook}
                  onChange={handleChange}
                  placeholder="username or page name"
                  className="input"
                />
              </div>

              {/* Snapchat */}
              <div>
                <label htmlFor="snapchat" className="block text-sm font-medium text-gray-700 mb-2">
                  <span className="inline-block w-5">👻</span> Snapchat
                </label>
                <input
                  type="text"
                  id="snapchat"
                  name="snapchat"
                  value={formData.snapchat}
                  onChange={handleChange}
                  placeholder="username"
                  className="input"
                />
              </div>

              {/* TikTok */}
              <div>
                <label htmlFor="tiktok" className="block text-sm font-medium text-gray-700 mb-2">
                  <span className="inline-block w-5">🎵</span> TikTok
                </label>
                <input
                  type="text"
                  id="tiktok"
                  name="tiktok"
                  value={formData.tiktok}
                  onChange={handleChange}
                  placeholder="@username"
                  className="input"
                />
              </div>

              {/* LinkedIn */}
              <div>
                <label htmlFor="linkedin" className="block text-sm font-medium text-gray-700 mb-2">
                  <span className="inline-block w-5">💼</span> LinkedIn
                </label>
                <input
                  type="text"
                  id="linkedin"
                  name="linkedin"
                  value={formData.linkedin}
                  onChange={handleChange}
                  placeholder="company-name or profile-name"
                  className="input"
                />
              </div>

              {/* YouTube */}
              <div>
                <label htmlFor="youtube" className="block text-sm font-medium text-gray-700 mb-2">
                  <span className="inline-block w-5">▶️</span> YouTube
                </label>
                <input
                  type="text"
                  id="youtube"
                  name="youtube"
                  value={formData.youtube}
                  onChange={handleChange}
                  placeholder="channel name or @handle"
                  className="input"
                />
              </div>
            </div>
          </div>

          {/* Package Selection Section */}
          <div className="pt-6 border-t border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              {isRTL ? 'اختيار الباقة (اختياري)' : 'Select Package (Optional)'}
            </h2>
            <p className="text-sm text-gray-600 mb-4">
              {isRTL 
                ? 'يمكنك اختيار باقة للعميل الآن أو إضافتها لاحقاً.'
                : 'You can select a package for the client now or add it later.'}
            </p>
            {loadingPackages ? (
              <div className="text-center py-4">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
              </div>
            ) : packages.length === 0 ? (
              <p className="text-sm text-gray-500">
                {isRTL 
                  ? 'لا توجد باقات متاحة. يمكنك إنشاء باقات من صفحة "الباقات".'
                  : 'No packages available. You can create packages from the "Packages" page.'}
              </p>
            ) : (
              <div>
                <select
                  id="package_id"
                  name="package_id"
                  value={formData.package_id}
                  onChange={handleChange}
                  className="input"
                >
                  <option value="">{isRTL ? '-- اختر باقة --' : '-- Select Package --'}</option>
                  {packages
                    .filter(pkg => pkg.is_active)
                    .map((pkg) => (
                      <option key={pkg.id} value={pkg.id}>
                        {isRTL && pkg.name_ar ? pkg.name_ar : pkg.name} 
                        {' '}({pkg.post_limit} {isRTL ? 'بوست' : 'posts'})
                        {pkg.price > 0 && ` - ${pkg.price} ${pkg.currency}`}
                      </option>
                    ))}
                </select>
                {formData.package_id && (
                  <p className="text-xs text-gray-500 mt-2">
                    {isRTL 
                      ? 'سيتم ربط هذه الباقة بالعميل تلقائياً'
                      : 'This package will be automatically assigned to the client'}
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className={`flex items-center justify-end pt-4 border-t border-gray-200 ${isRTL ? 'space-x-reverse space-x-3' : 'space-x-3'}`}>
            <Button
              type="button"
              variant="secondary"
              onClick={() => navigate(-1)}
              disabled={submitting}
            >
              {isRTL ? 'إلغاء' : 'Cancel'}
            </Button>
            <Button
              type="submit"
              variant="primary"
              loading={submitting}
              disabled={submitting}
            >
              {isRTL ? 'إنشاء العميل' : 'Create Client'}
            </Button>
          </div>
        </Card>
      </form>

      <div className="text-sm text-gray-500 text-center">
        <p>💡 {isRTL 
          ? 'نصيحة: بعد إنشاء العميل، يمكنك إضافة باقات أخرى أو إنشاء منشورات له.'
          : 'Tip: After creating the client, you can add more packages or create posts for them.'}
        </p>
      </div>
    </div>
  );
};

export default CreateClientPage;
